import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'

import { StorageAPI } from '@makyoapp/makyo-sdk'
import { StorageFoldersAPI } from '@makyoapp/makyo-sdk/api/Storage'
import { FileCategory } from '@makyoapp/makyo-sdk/models/Files'
import { Folder as FolderIcon } from '@makyoapp/makyo-icon/ficon'
import { Grid16, Plus16, Ul16 } from '@makyoapp/makyo-icon/icon'
import {
  Button,
  ButtonColor,
  ButtonVariant,
  DeleteModal,
  Field,
  Label,
  ModalGrid,
  SelectDropdown,
} from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useGetFolders } from '@/resolvers/org/storage/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useStorageContext } from '@/context/StorageContext'
import { useUploadContext } from '@/context/UploadContext'
import { ACCEPTED_FILE_TYPES } from '@/shared/constants'
import { SaveButton } from '@/components/SaveButton'

export function StorageAction() {
  const { t } = useTranslation()
  const { currentOrg } = useCurrentOrgContext()
  const { onChange } = useUploadContext()
  const {
    checkedFiles,
    setCheckedFiles,
    checkedFolders,
    setCheckedFolders,
    isGridView,
    setIsGridView,
    refreshStorage,
    currentFolderId,
  } = useStorageContext()

  const queryClient = useQueryClient()

  // Derived
  const isAnyItemSelected = checkedFiles.length > 0 || checkedFolders.length > 0
  const totalSelected = checkedFiles.length + checkedFolders.length

  const [isDeleting, setIsDeleting] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState('')

  // Access org storage folder
  const storageFoldersQuery = useGetFolders('')
  const storageFolders = useMemo(() => storageFoldersQuery.data || [], [storageFoldersQuery.data])
  const availableFolders = useMemo(() => {
    return [
      {
        id: 'root',
        name: 'Root',
      },
      ...storageFolders.map((folder) => ({
        id: folder._id,
        name: folder._Name,
      })),
    ]
  }, [storageFolders])

  // Determine if we have any folders selected
  const hasFoldersSelected = checkedFolders.length > 0

  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false)

  // Handle deleting folders
  const handleDeleteFolders = async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      await Promise.all(checkedFolders.map((folder) => StorageFoldersAPI.ReqDelete(folder._id, '')))

      await refreshStorage()

      toasted(t(`${checkedFolders.length} folder${checkedFolders.length > 1 ? 's' : ''} deleted successfully`))
      setCheckedFolders([])
      setShowDeleteFolderModal(false)
      // 
    } catch (error) {
      console.error('Error deleting folders:', error)
      toasted(t('Failed to delete folders'))
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle deleting files
  const handleDeleteFiles = async () => {
    if (isDeleting) return

    setIsDeleting(true)

    try {
      // Delete files using StorageAPI.ReqDeleteMany
      await StorageAPI.ReqDeleteMany({
        _Files: checkedFiles.map((file) => file._id),
      })

      // Refresh storage to update UI
      await refreshStorage()

      // Show success message
      toasted(t(`${checkedFiles.length} file${checkedFiles.length > 1 ? 's' : ''} deleted successfully`))
      setCheckedFiles([])
    } catch (error) {
      console.error('Error deleting files:', error)
      toasted(t('Failed to delete files'))
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle moving selected items to folder
  const handleMoveToFolder = async () => {
    if (!selectedFolderId || !currentOrg?.id) return

    setIsMoving(true)

    try {
      const folderId = selectedFolderId === 'root' ? '' : selectedFolderId

      // handle move files to folder
      const moveFilesPromise = () => {
        if (checkedFiles.length > 0) {
          return StorageAPI.ReqMoveFilesToFolder(folderId, {
            _OrgID: currentOrg.id,
            _Files: checkedFiles.map((item) => item._id),
          })
        }
      }

      // handle move folders to folder
      const moveFoldersPromises = () => {
        if (checkedFolders.length > 0) {
          return checkedFolders.map((f) => StorageFoldersAPI.ReqMove(f._id, folderId))
        }
      }

      await Promise.all([moveFilesPromise(), moveFoldersPromises()])

      // Invalidate the query to reflect the new data
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.orgStorageFolders, currentOrg?.id, folderId],
      })
      // Refresh data to reflect
      await refreshStorage()

      const totalItemsMoved = checkedFiles.length + checkedFolders.length

      // Show success message
      toasted(t(`${totalItemsMoved} item${totalItemsMoved > 1 ? 's' : ''} moved successfully`))

      // Close modal and reset selection
      setIsMoveModalOpen(false)
      setSelectedFolderId('')
      setCheckedFiles([])
      setCheckedFolders([])
    } catch (error) {
      console.error('Error moving items:', error)
      toasted(t('Failed to move items'))
    } finally {
      setIsMoving(false)
    }
  }

  const handleBulkDelete = () => {
    // If we have folders selected, show the delete confirmation modal
    if (hasFoldersSelected) {
      setShowDeleteFolderModal(true)
      return
    }
    // Otherwise just delete files directly (can add a simple confirmation here too if desired)
    handleDeleteFiles()
  }

  return (
    <>
      {/** Move Button */}
      <SaveButton
        isLoading={isMoving}
        colorStyle={ButtonColor.Tertiary}
        disabled={!isAnyItemSelected}
        onClick={() => {
          setIsMoveModalOpen(true)
          setSelectedFolderId('root')
        }}
        label={t('Move to folder')}
      />

      {/** Delete BUtton */}
      <SaveButton
        isLoading={isDeleting}
        colorStyle={ButtonColor.Danger}
        disabled={!isAnyItemSelected}
        onClick={handleBulkDelete}
        label={t('Delete Selected')}
      />

      <label className='cursor-pointer relative'>
        <input
          className='sr-only'
          type='file'
          accept={ACCEPTED_FILE_TYPES.join(',')}
          multiple
          onChange={(e) => {
            if (currentOrg) {
              onChange(e, currentOrg.id, currentFolderId)
            }
            e.target.value = ''
          }}
        />
        <Button className='pointer-events-none'>
          <Plus16 />
          <Trans>Upload New</Trans>
        </Button>
      </label>
      <Button noPadding className='w-9 h-9' variant={ButtonVariant.Ghost} onClick={() => setIsGridView(!isGridView)}>
        {isGridView ? <Grid16 /> : <Ul16 />}
      </Button>

      {/** Move to Folder Modal */}
      <ModalGrid
        show={isMoveModalOpen}
        title='Move to Folder'
        onCancel={() => {
          setIsMoveModalOpen(false)
          setSelectedFolderId('')
        }}
        onConfirm={handleMoveToFolder}
        disabled={!selectedFolderId || isMoving}
        isLoading={isMoving}
        primaryTrailing={isMoving ? 'Moving...' : 'Move'}
        showHeader={true}
        showCancel={true}
        modalId='move-items-modal'
        submitId='move-items-submit'
        cancelId='move-items-cancel'
        bodyProps={{
          className: 'p-6',
        }}
      >
        <Field defaultSize={false} noPad className='min-h-[3rem]'>
          <Label defaultSize={false} className={clsx('lbl-sm')}>
            {t('Select Destination Folder')}
          </Label>
          <SelectDropdown
            id='menu-item-preview-setting-trailing'
            value={selectedFolderId}
            onChange={(value) => setSelectedFolderId(value)}
            options={availableFolders.map((folder) => folder.id)}
            disabled={storageFoldersQuery.isLoading}
            renderOption={(folderId) => {
              const folderLabel = availableFolders.find((folder) => folder.id === folderId)?.name || ''
              return (
                <div className='flex items-center gap-2'>
                  <FolderIcon width={16} height={16} className='opacity-80' />
                  <span>{folderLabel}</span>
                </div>
              )
            }}
          />
        </Field>
        <div className='mt-4 text-slate-400'>
          Moving {totalSelected} selected item{totalSelected !== 1 ? 's' : ''} to the selected folder.
        </div>
      </ModalGrid>

      {/* Delete Folder Confirmation Modal */}
      <DeleteModal
        show={showDeleteFolderModal}
        onHide={() => setShowDeleteFolderModal(false)}
        onSubmit={handleDeleteFolders}
        onCancel={() => setShowDeleteFolderModal(false)}
        title={t('Delete Folder')}
        withCancel={true}
        cancelLabel={t('Cancel')}
        continueLabel={t('Yes, Delete')}
        isLoading={isDeleting}
        id='delete-folder-modal'
      >
        <p>
          {checkedFolders.length === 1
            ? t('Are you sure you want to delete this folder and all its contents? This action cannot be undone.')
            : t(
                'Are you sure you want to delete these {count} folders and all their contents? This action cannot be undone.',
                { count: checkedFolders.length }
              )}
        </p>
      </DeleteModal>
    </>
  )
}
